import { FC } from 'react';
import { createFragmentContainer, graphql } from 'react-relay/legacy';
import { ImageTextOverlay } from 'dibs-elements/exports/ImageTextOverlay';
import { FormattedMessage } from 'dibs-react-intl';
import { suspendWhileImageLoads } from './suspendWhileImageLoads';
import { ImageMultiSkuOverlay_item$data } from './__generated__/ImageMultiSkuOverlay_item.graphql';
import { ImageMultiSkuOverlay_sku$data } from './__generated__/ImageMultiSkuOverlay_sku.graphql';

type Props = {
    imageAlt?: string;
    imageClass?: string;
    overlayClass?: string;
    messageSize?: 'default' | 'small';
    item?: ImageMultiSkuOverlay_item$data | null;
    sku?: ImageMultiSkuOverlay_sku$data | null;
    onImageLoad?: () => void;
    useSuspense?: boolean;
};

const ImageMultiSkuOverlayComponent: FC<Props> = ({
    imageAlt,
    imageClass,
    overlayClass,
    messageSize,
    item,
    onImageLoad,
    sku,
    useSuspense,
}) => {
    const skuImagePath = sku?.ImageMultiSkuOverlayImageSrc;
    const itemImagePath = item?.ImageMultiSkuOverlayImageSrc;
    const isRingItem = item?.isRingItem;
    const imageSrc = skuImagePath || itemImagePath || '';

    if (useSuspense) {
        suspendWhileImageLoads(imageSrc);
    }

    return (
        <ImageTextOverlay
            showOverlay={!!item?.isMultiSku && !isRingItem && !skuImagePath}
            imagePath={imageSrc}
            message={
                messageSize === 'small' ? (
                    <FormattedMessage
                        id="dc.imageMultiSkuOverlay.messageSmall"
                        defaultMessage="No Image"
                    />
                ) : (
                    <FormattedMessage
                        id="dc.imageMultiSkuOverlay.message"
                        defaultMessage="Image Unavailable"
                    />
                )
            }
            messageSize={messageSize}
            onImageLoad={onImageLoad}
            imageAlt={imageAlt}
            imageClass={imageClass}
            overlayClass={overlayClass}
        />
    );
};

const ImageMultiSkuOverlay = createFragmentContainer(ImageMultiSkuOverlayComponent, {
    item: graphql`
        fragment ImageMultiSkuOverlay_item on Item
        @argumentDefinitions(imageSize: { type: "ImageVersionTypeName!", defaultValue: large }) {
            ImageMultiSkuOverlayImageSrc: firstPhotoWebPath(size: $imageSize)
            isMultiSku
            isRingItem
        }
    `,
    sku: graphql`
        fragment ImageMultiSkuOverlay_sku on ItemSkuType
        @argumentDefinitions(imageSize: { type: "ImageVersionTypeName!", defaultValue: large }) {
            ImageMultiSkuOverlayImageSrc: firstPhotoWebPath(size: $imageSize)
        }
    `,
});

export { ImageMultiSkuOverlayComponent, ImageMultiSkuOverlay };
